import { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LOS_GRID_EVENT } from "../Lit/acc-wc-views/src/acc-wc-view-los-indented/enum.js";
import { mapAttributes } from "../../common/util/config";
import { storedLocale } from "../Authentication/util";
import { useAppDispatch, useAppSelector } from "../../store";
import { SELECTOR_TYPE, updateSelectorType } from "../../reducers/period";
import { ROUTE_PATHS } from "../../common/enums/routing-enums";
import { showProfile } from "../../reducers/profile";
import { toggleExpandedNode, clearTreeViewState } from "../../reducers/treeViewUiState";
import { LOSExportModal } from "../ServerSideExport/LOSExportModal/LOSExportModal";
import { BonusInfoFooter } from "../../common/components/BonusInfoFooter/BonusInfoFooter";
import { PageTitleSection } from "../../common/components/PageTitleSection/PageTitleSection";
import { OpenExportV2ModalEvent } from "../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import ServerSideExportCompletedNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import ServerSideExportInProgressNotify from "../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import { LosToolbar } from "./LosToolbar/LosToolbar";
import { IconBack } from "../../common/icons/IconBack/IconBack";
import defaultContent from "../../content/navigation";
import defaultLosContent from "../../content/los";
import useWindowSize from "../../common/hooks/useWindowSize";
import breakpoints from "../../common/scss/breakpoints.module.scss";
import "./RwLos.scss";
import { FullPageComponentLoader } from "../../common/components/FullPageComponentLoader/FullPageComponentLoader";
import { useImportLitComponent, LitComponent } from "../../common/hooks/useImportLitComponent";
import { useLitRouterPopstateGuard } from "../../common/hooks/useLitRouterPopstateGuard";
import TreeView, { LOS_GRID_CONFIG } from "../TreeView/TreeView.js";
import type { IsolatedAbo } from "../TreeView/TreeView.js";
import type { TreeNodeData } from "../TreeView/TreeViewDataLayer";
import { useTreeViewPrefetch } from "../../services/TreeView/TreeViewAPI";
import { getLosParamsForGrid } from "../TreeView/TreeViewDataLayer";
import { basePath as losArBasePath } from "../Lit/acc-wc-los-ar/src/routes/path.js";
import { useAccBootStore } from "../../common/hooks/useAccBootStore";
import { flattenLosSearchRecords } from "./LosToolbar/LosToolbar";

export default function LOS() {
  const { t } = useTranslation(["navigation", "los"]);
  const locale = storedLocale();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { configuration } = useAppSelector((state) => state.boot);
  const { width } = useWindowSize();
  // TopNavigationBar (and its own LOS title) unmounts below this width in favor of
  // MobileNavigationBar, which renders no title — render our own title only then,
  // to avoid a duplicate #title-portal render alongside TopNavigationBar on desktop.
  const showMobileTitle = width !== undefined && Number(width) < Number(breakpoints.desktop);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { state, search } = location;
  const [isTreeView, setIsTreeView] = useState(false);
  const [isolatedAbo, setIsolatedAbo] = useState<IsolatedAbo | null>(null);

  useEffect(() => {
    if (isolatedAbo !== null) {
      window.scrollTo(0, 0);
    }
  }, [isolatedAbo]);

  // Search selection is only remembered locally by each view (Lit list component
  // stays mounted so it keeps its own highlight; TreeView's selectedAbo resets on
  // every mount) - lifting it here lets a search made in one view survive a
  // switch to the other, since TreeView fully unmounts on toggle-off.
  const [selectedSearchAffAbo, setSelectedSearchAffAbo] = useState<string | null>(null);
  // Bumped on every search-bar clear to signal TreeView to drop its own search
  // selection/highlight (and close the node modal) - see clearSelectionToken on
  // TreeView's props for why a counter instead of a boolean.
  const [clearSelectionToken, setClearSelectionToken] = useState(0);
  const queryParams = new URLSearchParams(search);
  const { isImported } = useImportLitComponent({ component: LitComponent.LOS_AR });

  let { targetAbo = "" } = state || {};
  if (!targetAbo) {
    targetAbo = queryParams.get("targetAbo") || "";
  }

  const { state: bootStoreState } = useAccBootStore();
  // rwlosSearchData is written by the Lit grid once its initial data load
  // completes (and again on isolate) - wait for a non-loading, populated set
  // before searching, or a stale/empty value left over from a previous LOS
  // visit can resolve (or fail to resolve) the record too early.
  useEffect(() => {
    if (!targetAbo || bootStoreState.loading) return;
    const record = flattenLosSearchRecords(bootStoreState.rwlosSearchData).find(
      (r) => String(r.abo ?? r.aboNumber ?? "") === targetAbo,
    );
    if (!record) return;
    document.dispatchEvent(
      new CustomEvent(LOS_GRID_EVENT.DATA_FIND, {
        bubbles: true,
        composed: true,
        detail: { record },
      }),
    );
  }, [targetAbo, bootStoreState.rwlosSearchData, bootStoreState.loading]);

  // This is for accessibility report scan purposes only - Start
  const manualOpenProfileModal = queryParams.get("showModal") === "profile";
  const manualOpenExportModal = queryParams.get("showModal") === "export";
  const { aff, abo, isoCountryCode } = useAppSelector((state) => state.boot.user);
  // Tree view follows the same rwlos.uiGrid flag as the list view (see getLosParamsForGrid)
  const treeLosParams = useMemo(() => getLosParamsForGrid(configuration.rwlos?.uiGrid), [configuration.rwlos?.uiGrid]);

  if (manualOpenProfileModal) {
    dispatch(showProfile(`${aff}-${abo}`));
  }
  // This is for accessibility report scan purposes only - End

  const [openExportModal, setExportModalState] = useState(manualOpenExportModal);
  const [modalOpenedByElement, setModalOpenedByElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    dispatch(updateSelectorType(SELECTOR_TYPE.MONTHLY));
  }, [dispatch]);

  // Prefetch tree view data so it's cached before the user toggles to tree view
  const prefetchTreeView = useTreeViewPrefetch("getTreeView");
  useEffect(() => {
    if (selectedPeriod && aff && abo && isoCountryCode && locale) {
      prefetchTreeView({
        Aff: aff,
        Abo: abo,
        Period: selectedPeriod,
        IsoCountryCode: isoCountryCode,
        Locale: locale,
        ...treeLosParams,
      });
    }
  }, [selectedPeriod, aff, abo, isoCountryCode, locale, prefetchTreeView, treeLosParams]);

  useLayoutEffect(() => {
    if (isImported) {
      const rwlos = document.getElementById("acc_wc_los_ar_component");
      if (rwlos && configuration.rwlos) {
        mapAttributes(rwlos, configuration.rwlos, {});
      }
    }
  }, [isImported, configuration.rwlos]);

  useEffect(() => {
    document.dispatchEvent(
      new CustomEvent("period-changed", {
        bubbles: true,
        composed: true,
        detail: {
          period: selectedPeriod,
        },
      }),
    );
  }, [selectedPeriod]);

  const showABOProfile = useCallback(
    (event: CustomEventInit) => {
      const previousElement =
        event.detail.target.parentElement.id === "rootAbo" ? event.detail.target.parentElement : event.detail.target;
      dispatch(
        showProfile({
          affAbo: event.detail.abo.affAbo,
          previousElement,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfile);
    return () => {
      window.removeEventListener(LOS_GRID_EVENT.ACTION_SEND_ABO, showABOProfile);
    };
  }, [showABOProfile]);

  // Mirrors expand/collapse raised by the Lit list view into the same Redux store
  // Tree Map reads on mount (see TreeView's cachedExpanded) - the counterpart to
  // handleTreeViewExpandChange below, which drives the list view from Tree Map's side.
  const handleListExpandChanged = useCallback(
    (event: Event) => {
      const { aff, abo, expanded, fromTreeViewMirror } =
        (event as CustomEvent<{ aff: number | string; abo: number; expanded: boolean; fromTreeViewMirror?: boolean }>)
          .detail || {};
      if (aff === undefined || abo === undefined) return;
      // List View's Lit grid reports aff zero-padded (e.g. "010"); Tree Map's node data
      // and its cache keys use the unpadded number (e.g. 10) - normalize here so the
      // Redux key matches what TreeView looks up, or the cache never hits.
      dispatch(toggleExpandedNode({ contextKey: "los", nodeId: `${Number(aff)}-${abo}`, expanded }));
      // A direct click in List View also fires this event - not just Tree Map's own
      // expandByAffAbo mirror calls draining through the same handler (including, for a
      // not-yet-loaded target, the extra ancestor nodes expandByAffAbo itself expands
      // along the way - each of those is also a mirror-call side effect, not a click). If
      // a Tree Map Expand All/Collapse All batch is still draining when the user clicks
      // directly in List View, its leftover queued calls aren't stale (same generation)
      // and keep replaying afterward, undoing the user's click (ABOT-13129). Only a real
      // List View click should invalidate the batch - the queue's own mirrored calls
      // only ever run while Tree Map is the active view, so gate on !isTreeView the same
      // way handleListExpandReset already does below. But isTreeView can flip to false
      // (user switches to List View) WHILE a batch is still mid-drain - at that point the
      // queue's own remaining mirror calls' echoes also see !isTreeView and would wrongly
      // look like a real click, bumping the generation and invalidating the rest of the
      // still-queued batch behind it (worse the deeper the target, since expandByAffAbo
      // must walk open more collapsed ancestors - each one its own echo - before reaching
      // it, which is why a deep root Expand All used to stall around the first couple of
      // levels). fromTreeViewMirror is stamped by expandByAffAbo itself (and inherited by
      // every ancestor-walk echo nested inside the same call), so it reliably marks every
      // echo of our own mirror call regardless of which node it's for - unlike comparing
      // against a single "current target" key, which missed the ancestor-walk echoes.
      if (!isTreeView && !fromTreeViewMirror) {
        expandBatchGenerationRef.current += 1;
      }
    },
    [dispatch, isTreeView],
  );

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.EXPAND_CHANGED, handleListExpandChanged);
    return () => {
      window.removeEventListener(LOS_GRID_EVENT.EXPAND_CHANGED, handleListExpandChanged);
    };
  }, [handleListExpandChanged]);

  // List View bulk-collapses (clearing search, removing a filter) reset its own
  // expand state directly without going through the per-node EXPAND_CHANGED path -
  // wipe the shared store too so Tree Map doesn't keep showing stale expansion.
  // But List View's Lit grid stays mounted (just display:none) while Tree Map is
  // active and reacts to the same shared accBootStore - so toggling a filter from
  // Tree Map's own UI (e.g. "Show International Downlines") also fires this reset
  // even though Tree Map's live expand state is already correct. Only clear the
  // cache when List View is the one actually being looked at, or every Tree Map
  // filter toggle wipes its own cache and (on the next remount) renders collapsed.
  const handleListExpandReset = useCallback(() => {
    if (isTreeView) return;
    dispatch(clearTreeViewState({ contextKey: "los" }));
    // Same rationale as handleListExpandChanged above - a bulk reset triggered
    // directly in List View (e.g. a filter's implicit collapse) must also invalidate
    // any still-draining Tree Map expand-all batch, or its leftover calls replay
    // afterward and re-expand what the reset just collapsed (ABOT-13129).
    expandBatchGenerationRef.current += 1;
  }, [dispatch, isTreeView]);

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.EXPAND_RESET, handleListExpandReset);
    return () => {
      window.removeEventListener(LOS_GRID_EVENT.EXPAND_RESET, handleListExpandReset);
    };
  }, [handleListExpandReset]);

  // Mirrors isolation state raised by the Lit list view so Tree Map stays in sync
  const handleIsolateChanged = useCallback((event: Event) => {
    const { affAbo, isolated, level } =
      (event as CustomEvent<{ affAbo: string; isolated: boolean; level?: number }>).detail || {};
    if (!isolated || !affAbo) {
      setIsolatedAbo(null);
      return;
    }
    const [aff, abo] = affAbo.split("-");
    setIsolatedAbo({ aff, abo, level: level ?? 1 });
  }, []);

  // Remembers the last search selection (from either view's search box, since they
  // share the same LosToolbar) so switching views can restore the highlight instead
  // of losing it when TreeView unmounts.
  const handleSearchFound = useCallback((event: Event) => {
    const record = (event as CustomEvent<{ record?: { affAbo?: string } }>).detail?.record;
    if (record?.affAbo) {
      setSelectedSearchAffAbo(record.affAbo);
    }
  }, []);

  useEffect(() => {
    document.addEventListener(LOS_GRID_EVENT.DATA_FIND, handleSearchFound);
    return () => {
      document.removeEventListener(LOS_GRID_EVENT.DATA_FIND, handleSearchFound);
    };
  }, [handleSearchFound]);

  useEffect(() => {
    window.addEventListener(LOS_GRID_EVENT.ISOLATE_CHANGED, handleIsolateChanged);
    return () => {
      window.removeEventListener(LOS_GRID_EVENT.ISOLATE_CHANGED, handleIsolateChanged);
    };
  }, [handleIsolateChanged]);

  const getIndentedLosGrid = useCallback(() => {
    const losArElement = document.getElementById("acc_wc_los_ar_component");
    return losArElement?.shadowRoot?.querySelector("#component")?.querySelector("acc-wc-view-los-indented") as
      | (HTMLElement & {
          isolateByAffAbo?: (v: string | null, level?: number) => Promise<void> | void;
          scrollToAffAbo?: (v: string) => Promise<void> | void;
          clearSearchResult?: () => void;
        })
      | null;
  }, []);

  // Resets every piece of state that remembers "the last searched ABO" when the
  // user clears the LOS search box - RwLos's own cross-view memory, TreeView's
  // selection/highlight (via clearSelectionToken), and the list grid's highlighted
  // row (its own __searchResult, unreachable any other way from outside).
  const handleSearchClear = useCallback(() => {
    setSelectedSearchAffAbo(null);
    setClearSelectionToken((token) => token + 1);
    const gridCard = getIndentedLosGrid();
    gridCard?.clearSearchResult?.();
  }, [getIndentedLosGrid]);

  // Drives Downline LOS from the Tree Map side into the Lit list view, which
  // has no attribute-driven entry point — call its exposed method imperatively instead.
  const setListCounselingView = useCallback(
    (abo: TreeNodeData | null) => {
      setIsolatedAbo(abo ? { aff: String(abo.aff).padStart(3, "0"), abo: String(abo.abo), level: abo.level } : null);
      const gridCard = getIndentedLosGrid();
      gridCard?.isolateByAffAbo?.(abo ? `${String(abo.aff).padStart(3, "0")}-${abo.abo}` : null, abo?.level);
    },
    [getIndentedLosGrid],
  );

  const handleTreeViewIsolateChange = useCallback(
    (abo: TreeNodeData | null) => {
      setListCounselingView(abo);
    },
    [setListCounselingView],
  );

  const handleUndoCounselingView = useCallback(async () => {
    if (!isolatedAbo) return;

    const counselingAffAbo = `${isolatedAbo.aff}-${isolatedAbo.abo}`;
    setIsolatedAbo(null);

    const gridCard = getIndentedLosGrid();
    await gridCard?.isolateByAffAbo?.(null);
    await gridCard?.scrollToAffAbo?.(counselingAffAbo);
  }, [getIndentedLosGrid, isolatedAbo]);

  // Mirrors an expand/collapse made in the Tree Map into the Lit list view, which has
  // no shared expand-state store with Tree Map - a no-op there if the target isn't
  // currently loaded/visible in the list. A recursive Expand Group fires one of these
  // per descendant, all synchronously and back to back - expandByAffAbo is async and
  // mutates the grid's internal data at the end of each call, so calling it concurrently
  // for multiple descendants races and can silently drop some of them. Chain calls onto
  // a running promise so each one starts only after the previous has fully applied.
  const expandQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Bumped by onExpandBatchStart (see TreeView) right before Expand All/Collapse All/a
  // recursive Expand Group starts enqueuing its calls. Each queued call captures the
  // generation it was enqueued under and skips itself if a newer batch has started since -
  // otherwise clicking Collapse All while Expand All's calls are still draining queues the
  // collapse behind them, so the grid keeps visibly re-expanding nodes after the user
  // already asked to collapse (nothing previously aborted the stale, still-running batch).
  const expandBatchGenerationRef = useRef(0);
  const handleTreeViewExpandBatchStart = useCallback(
    (changes: Array<{ aff: number; abo: number; expanded: boolean }>) => {
      expandBatchGenerationRef.current += 1;
      const generation = expandBatchGenerationRef.current;

      // Resolve every target's lazily-loaded (shadowChildren) structural data UP FRONT,
      // in parallel, before the per-node queue below starts draining them one at a time.
      // Without this, each shadowChildren node's own network fetch only starts when its
      // turn comes up in the serialized queue - racing against ~100+ other already-queued
      // calls can leave it stuck (ABOT-13129 follow-up: this was the real cause of
      // expand-all stopping partway through on a fresh load, recovering only after
      // several manual expand/collapse cycles gave each node's fetch a clean, unburdened
      // turn). Enqueued onto the SAME expandQueueRef as every mirror call below, ahead of
      // all of them (this runs synchronously before any onExpandChange call for this
      // batch reaches handleTreeViewExpandChange), so it still respects generation
      // invalidation like everything else in the queue.
      const expandTargets = changes.filter((c) => c.expanded).map((c) => `${String(c.aff).padStart(3, "0")}-${c.abo}`);
      if (expandTargets.length > 0) {
        expandQueueRef.current = expandQueueRef.current.then(async () => {
          if (generation !== expandBatchGenerationRef.current) return undefined;
          const losArElement = document.getElementById("acc_wc_los_ar_component");
          const gridCard = losArElement?.shadowRoot
            ?.querySelector("#component")
            ?.querySelector("acc-wc-view-los-indented") as
            | (HTMLElement & { prefetchShadowChildren?: (affAbos: string[]) => Promise<void> })
            | null;
          await gridCard?.prefetchShadowChildren?.(expandTargets);
          return undefined;
        });
      }
    },
    [],
  );

  const handleTreeViewExpandChange = useCallback((abo: { aff: number; abo: number }, expanded: boolean) => {
    const generation = expandBatchGenerationRef.current;
    expandQueueRef.current = expandQueueRef.current.then(() => {
      if (generation !== expandBatchGenerationRef.current) return undefined;
      const losArElement = document.getElementById("acc_wc_los_ar_component");
      const gridCard = losArElement?.shadowRoot
        ?.querySelector("#component")
        ?.querySelector("acc-wc-view-los-indented") as
        | (HTMLElement & { expandByAffAbo?: (v: string, expanded: boolean) => Promise<void> | void })
        | null;
      return gridCard?.expandByAffAbo?.(`${String(abo.aff).padStart(3, "0")}-${abo.abo}`, expanded) ?? undefined;
    });
  }, []);

  useLitRouterPopstateGuard(losArBasePath, ROUTE_PATHS.LOS);

  // Intercept tree view toggle before the Lit router processes it — without this,
  // the router swaps the outlet content and toggling back renders an empty grid.
  const isTreeViewRef = useRef(false);

  const handleTreeViewToggle = useCallback((event: Event) => {
    const customEvent = event as CustomEvent<{ pathname: string }>;
    const pathname = customEvent.detail?.pathname;
    if (!pathname) return;

    const isTreeViewPath = pathname === "/$__los-ar/accLosTree";

    if (isTreeViewPath) {
      // Toggling tree view ON — intercept so the Lit router doesn't swap the outlet
      event.stopPropagation();
      isTreeViewRef.current = true;
      setIsTreeView(true);
    } else if (isTreeViewRef.current && !pathname.startsWith("/$__los-ar/")) {
      // Toggling tree view OFF (navigating to a non-Lit-internal path) — intercept
      event.stopPropagation();
      isTreeViewRef.current = false;
      setIsTreeView(false);
    }
    // All other router events (error pages, other Lit navigations) pass through untouched
  }, []);

  useEffect(() => {
    window.addEventListener("acc-es-router-go", handleTreeViewToggle, true);
    return () => {
      window.removeEventListener("acc-es-router-go", handleTreeViewToggle, true);
    };
  }, [handleTreeViewToggle]);

  // Hide/show Lit component's main content based on tree view state. Positioned
  // off-screen (not display:none) while Tree Map is active - lit-virtualizer computes
  // its visible-row range from getBoundingClientRect() on this grid's own container
  // (see Virtualizer._updateView), which collapses to zero size under display:none.
  // Any Tree Map mirror call (expandByAffAbo) that runs while hidden - e.g. root Expand
  // Group's whole batch, since the user can switch views again before it finishes
  // draining - computes its layout against that bogus zero viewport, corrupting the
  // virtualizer's internal position/size state in a way a later repaint can't fully
  // recover from (deeper rows stay stuck not-rendered even though __data/isExpanded is
  // already correct; it only ever recovered after several manual expand/collapse
  // cycles while actually visible). Keeping a real, if invisible, layout box the whole
  // time means every mirror call's own layout pass is correct from the start, so no
  // post-hoc repaint-on-switch is needed (ABOT-13129 follow-up).
  useEffect(() => {
    const losArElement = document.getElementById("acc_wc_los_ar_component");
    if (losArElement?.shadowRoot) {
      const mainContent = losArElement.shadowRoot.querySelector("#component") as HTMLElement;
      if (mainContent) {
        if (isTreeView) {
          // Capture its current (real, in-flow) box size before taking it out of flow -
          // position: absolute/fixed would otherwise shrink-wrap it to content size,
          // which is just as wrong for the virtualizer's width-based layout as
          // display:none's zero size was.
          const { width, height } = mainContent.getBoundingClientRect();
          mainContent.style.width = `${width}px`;
          mainContent.style.height = `${height}px`;
          // position: fixed (not absolute) - an absolutely-positioned element still
          // contributes its full box to its containing block's (.RwLos__wrapper,
          // position: relative) scrollable overflow even when shifted far off-screen
          // horizontally via `left`. With a tall list (many expanded rows) that extra
          // vertical space let the page scroll well past the real, visible content into
          // this invisible box - landing on an empty area with nothing to see (ABOT-13945
          // regression from ABOT-13915). Fixed positioning anchors to the viewport
          // instead of the containing block, so it never extends document scroll height,
          // while still keeping the real, non-zero getBoundingClientRect() the
          // virtualizer needs.
          mainContent.style.position = "fixed";
          mainContent.style.visibility = "hidden";
          mainContent.style.pointerEvents = "none";
          mainContent.style.left = "-99999px";
          mainContent.style.top = "0";
        } else {
          mainContent.style.position = "";
          mainContent.style.visibility = "";
          mainContent.style.pointerEvents = "";
          mainContent.style.left = "";
          mainContent.style.top = "";
          mainContent.style.width = "";
          mainContent.style.height = "";
        }
      }
    }
  }, [isTreeView, isImported]);

  // Re-center the list view on the last search selection when switching back from Tree
  // Map - the reverse of what selectedSearchAffAbo already does for Tree Map on mount
  // (see initialSelectedAboNumber above). List view's Lit grid stays mounted the whole
  // time, so unlike Tree Map it never re-selects on its own; drive it imperatively.
  useEffect(() => {
    if (isTreeView || !selectedSearchAffAbo) return;
    const losArElement = document.getElementById("acc_wc_los_ar_component");
    const gridCard = losArElement?.shadowRoot
      ?.querySelector("#component")
      ?.querySelector("acc-wc-view-los-indented") as
      | (HTMLElement & { scrollToAffAbo?: (affAbo: string) => Promise<void> | void })
      | null;
    gridCard?.scrollToAffAbo?.(selectedSearchAffAbo);
  }, [isTreeView, selectedSearchAffAbo]);

  const showExportModal = useCallback((event: CustomEventInit) => {
    setExportModalState(Boolean(event.detail.open));
    setModalOpenedByElement(event.detail.target);
  }, []);

  useEffect(() => {
    window.addEventListener(OpenExportV2ModalEvent, showExportModal);
    return () => {
      window.removeEventListener(OpenExportV2ModalEvent, showExportModal);
    };
  }, [showExportModal]);

  const notifyDownloadLinkOnClick = () => {
    setExportModalState(true);
  };

  const closeExportModal = () => {
    setExportModalState(false);
    setTimeout(() => {
      if (modalOpenedByElement) {
        modalOpenedByElement.focus();
      }
    }, 100);
  };

  if (!isImported) {
    return <FullPageComponentLoader />;
  }

  // configuration.rwlos.uiHeader is a comma-separated string of flag names
  // (e.g. "uiShowExport,uiShowKpiConfig,uiShowSearch,...").
  // Convert it to the boolean flags object that LosToolbar expects.
  const uiHeaderFlags = (configuration.rwlos.uiHeader ?? "").split(",").reduce<Record<string, boolean>>((acc, flag) => {
    const key = flag.trim();
    if (key) acc[key] = true;
    return acc;
  }, {});

  const renderLosAr = () => {
    // Strip uiShowKpiConfig from the Lit component's uiHeader — the React LosToolbar
    // handles KPI selection now, so we don't want the Lit KPI modal to also appear.
    const litUiHeader = (configuration.rwlos.uiHeader ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s && s !== "uiShowKpiConfig")
      .join(",");

    return (
      <acc-wc-los-ar
        data-testid="rwlos"
        id="acc_wc_los_ar_component"
        hidePeriodSelector
        hide-header=""
        period={selectedPeriod}
        locale={locale}
        losviewtype="los"
        uiGrid={configuration.rwlos.uiGrid}
        uiKpis={configuration.rwlos.uiKpis}
        uiHeader={litUiHeader}
        uiGridExternalLinks={JSON.stringify(configuration.rwlos.uiGridExternalLinks)}
      ></acc-wc-los-ar>
    );
  };

  return (
    <main>
      {showMobileTitle && (
        <PageTitleSection
          title={`${t("linkTitle.losShort", (defaultContent.linkTitle as Record<string, string>).losShort)}`}
        />
      )}
      <div id="rwlos-wrapper" className="RwLos__wrapper">
        <LosToolbar
          uiHeader={uiHeaderFlags}
          availableKpis={(configuration.uiKpis?.los ?? []).filter((kpi: string) => kpi !== "isFirstContractYear")}
          losViewType="los"
          isImported={isImported}
          clearSearchSelection={handleSearchClear}
        />
        {isolatedAbo !== null && !isTreeView && (
          <div className="RwLos__counselingHeader">
            <button
              className="RwLos__undoCounselingView"
              onClick={handleUndoCounselingView}
              disabled={bootStoreState.loading}
              type="button"
            >
              <IconBack width="0.5rem" height="0.75rem" color="#38539a" />
              {t("toolbar.undoDownlineLos", defaultLosContent.toolbar.undoDownlineLos, { ns: "los" })}
            </button>
          </div>
        )}
        {renderLosAr()}
        {isTreeView && (
          <TreeView
            callData={true}
            period={selectedPeriod}
            params={treeLosParams}
            filterLevel2International={true}
            contextKey="los"
            config={{ [LOS_GRID_CONFIG.UI_MENU_SHOW_ISOLATE]: true }}
            isolatedAbo={isolatedAbo}
            onIsolateChange={handleTreeViewIsolateChange}
            onExpandChange={handleTreeViewExpandChange}
            onExpandBatchStart={handleTreeViewExpandBatchStart}
            initialSelectedAboNumber={selectedSearchAffAbo?.split("-")[1] ?? null}
            clearSelectionToken={clearSelectionToken}
            enableFullscreen
            uiHeader={uiHeaderFlags}
          />
        )}
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify moduleId={"los"} notifyDownloadLinkOnClick={notifyDownloadLinkOnClick} />
      <LOSExportModal open={openExportModal} onCloseCallbackFn={closeExportModal} view="rwlos" />
      <BonusInfoFooter hideDate />
    </main>
  );
}
